import { build } from "./build";
import { types } from "./types";

import chokidar from "chokidar";
import debounce from "lodash.debounce";
import { logger } from "../../shared/logger";
import {
  bold,
  cyan,
  gray,
  yellow,
  red,
  bgMagenta,
  white,
  whiteBright,
} from "colorette";
import { DEFAULT_ENV_FILE, DEFAULT_ENVX_FILE } from "../../shared/constants";

interface WatchOptions {
  input?: string;
  output?: string;
  metaFilePath?: string;
  typesOutput?: string;
  silent?: boolean;
  noTypes?: boolean;
  noBuild?: boolean;
}

const REGENERATE_MS = 750;
const INDENT = "   ";

export async function watch(options: WatchOptions) {
  const inputFile = options.input || DEFAULT_ENVX_FILE;
  const metaPath = options.metaFilePath || ".";
  const outputFile = options.output || DEFAULT_ENV_FILE;
  const typesOut = options.typesOutput || "envx.ts";

  if (!options.silent) {
    logger.block(
      `${bold(cyan("🌱  Starting watcher"))}\n${gray(
        `Watching for changes on ${white(bgMagenta(inputFile))}\n`
      )}`
    );
  }

  async function runBuildAndTypes() {
    if (!options.noBuild) {
      if (!options.silent) logger.log(bold(cyan("[Build]")));
      await build(
        {
          input: inputFile,
          output: outputFile,
          metaFilePath: metaPath,
          overwrite: true,
        },
        {
          showPrefix: false,
          addSpace: INDENT,
        }
      );
    }

    if (!options.noTypes) {
      if (!options.silent) logger.log(bold(cyan("[Types]")));
      await types(
        {
          input: inputFile,
          typesOutput: typesOut,
          metaFilePath: metaPath,
        },
        {
          showPrefix: false,
          addSpace: INDENT,
        }
      );
    }
  }

  try {
    await runBuildAndTypes();
  } catch (err) {
    logger.error(
      `${red("✖")} Initial generation failed: ${bold(
        err instanceof Error ? err.message : String(err)
      )}`
    );
  }

  const regenerate = debounce(async () => {
    if (!options.silent) {
      logger.log(gray("\n────────────────────────────\n"));
      logger.log(bold(yellow("⚡ Change detected! Regenerating...\n")));
    }

    try {
      await runBuildAndTypes();
      if (!options.silent) {
        logger.log(
          bold(whiteBright("\n✅ Regeneration completed successfully!\n"))
        );
      }
    } catch (err) {
      logger.error(
        `${red("✖")} Error during regeneration: ${bold(
          err instanceof Error ? err.message : String(err)
        )}`
      );
    }
  }, REGENERATE_MS);

  chokidar.watch(inputFile, { ignoreInitial: true }).on("change", regenerate);
}
